import mixpanel from 'mixpanel-browser';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getMe, type MeResponse, type MeUser } from '@/services/auth';

/**
 * User store — caches the /api/me response for the lifetime of the SPA session.
 *
 * fetch()   — returns the cached user if already loaded, otherwise calls the API once.
 *             Concurrent callers share the in-flight request: a view and the header's
 *             UserMenu mount in the same tick, and without this they each fired /api/me.
 * refresh() — forces a re-fetch regardless of cache (call after token refresh or
 *             any server-side change that should be reflected immediately).
 * clear()   — resets the cache (called on logout).
 */
export const useUserStore = defineStore('user', () => {
  const me = ref<MeResponse | null>(null);
  const loading = ref(false);

  const user = computed<MeUser | null>(() => me.value?.user ?? null);
  const permissions = computed<string[]>(() => me.value?.permissions ?? []);

  // Not a ref: nothing renders from it, it only dedupes concurrent fetch() calls.
  let inFlight: Promise<MeUser> | null = null;

  async function fetch(): Promise<MeUser> {
    if (user.value) return user.value;
    if (inFlight) return inFlight;

    loading.value = true;
    const request: Promise<MeUser> = getMe()
      .then(response => {
        me.value = response;
        if (response.user?.uuid) {
          try {
            mixpanel.identify(response.user.uuid);
          } catch {
            /* not initialised yet */
          }
        }
        return response.user;
      })
      .finally(() => {
        // Only if refresh()/clear() has not already started another one.
        if (inFlight === request) inFlight = null;
        loading.value = false;
      });
    inFlight = request;

    return request;
  }

  async function refresh(): Promise<MeUser> {
    me.value = null;
    inFlight = null; // don't hand back the request started before the change
    return fetch();
  }

  function clear(): void {
    me.value = null;
    inFlight = null;
    try {
      mixpanel.reset();
    } catch {
      /* not initialised */
    }
  }

  return { me, user, permissions, loading, fetch, refresh, clear };
});
